import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import LetterGlitch from './LetterGlitch';
import './NetworkScanLive.css';

const PHASES_QUEUED = [
  'Validating allowlist and targets…',
  'Allocating scan job on the API…',
];

const PHASES_RUNNING = [
  'Resolving addresses in scope…',
  'Host discovery (nmap -sn when enabled)…',
  'TCP port probes / service mapping…',
  'Writing discovery JSON and merging CMDB…',
];

/**
 * Live visualization while a network scan job is in progress.
 */
export function NetworkScanLive({ status, jobId, targetsLabel, includeNoPorts, startedAt }) {
  const [elapsed, setElapsed] = useState(0);
  const [tick, setTick] = useState(0);

  const active = status === 'queued' || status === 'running';

  const t0 = useMemo(() => {
    if (typeof startedAt === 'number' && !Number.isNaN(startedAt)) return startedAt;
    return null;
  }, [startedAt]);

  useEffect(() => {
    if (!active) return undefined;
    const base = t0 ?? Date.now();
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - base) / 1000));
      setTick((x) => x + 1);
    }, 500);
    return () => clearInterval(id);
  }, [active, t0]);

  const phaseIndex = useMemo(() => {
    const list = status === 'queued' ? PHASES_QUEUED : PHASES_RUNNING;
    if (!list.length) return 0;
    return tick % list.length;
  }, [status, tick]);

  const lines = useMemo(() => {
    const phaseList = status === 'queued' ? PHASES_QUEUED : PHASES_RUNNING;
    const out = [];
    const n = Math.min(phaseList.length, 4);
    for (let i = 0; i < n; i += 1) {
      const idx = (phaseIndex + i) % phaseList.length;
      out.push(phaseList[idx]);
    }
    return out;
  }, [status, phaseIndex]);

  if (!active) return null;

  return (
    <div
      className="network-scan-live mb-4 flex flex-col gap-4 rounded-xl border border-primary/20 bg-gradient-to-b from-primary/5 to-transparent p-4"
      role="status"
      aria-live="polite"
      aria-label="Network scan in progress"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/60 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary" />
          </span>
          Live scan
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-normal text-primary">
            {status === 'queued' ? 'queued' : 'running'}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className="font-mono">{jobId || '—'}</span>
          <span className="font-mono tabular-nums">{String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}</span>
        </div>
      </div>

      {/* Glitch as a tinted background so it's impressive but readable */}
      <div className="relative isolate w-full h-[clamp(190px,32vh,340px)] overflow-hidden rounded-2xl border border-primary/30 shadow-lg shadow-primary/10 ring-1 ring-primary/15">
        <div className="absolute inset-0 opacity-40">
          <LetterGlitch glitchSpeed={75} centerVignette outerVignette={false} smooth />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-background/80" />
        <div className="relative flex h-full items-end justify-between gap-3 p-3">
          <div className="min-w-0">
            <div className="text-[11px] font-medium text-primary/90">Current phase</div>
            <div className="truncate font-mono text-[11px] text-foreground/90">{lines[0]}</div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="inline-flex h-2 w-2 rounded-full bg-primary/90 shadow-[0_0_16px_rgba(132,0,255,.55)]" />
            {includeNoPorts ? 'Discovery + hosts w/ no open ports' : 'Discovery focused'}
          </div>
        </div>
      </div>

      <p className="text-center text-[11px] leading-snug text-muted-foreground/90">
        Time depends on scope: a full <span className="font-mono">/24</span> with host discovery + port probes often runs{' '}
        <span className="text-foreground/80">several minutes</span>.
      </p>

      <div className="space-y-2">
        <div className="network-scan-live__bar" />
        <p className="text-center text-xs text-muted-foreground">
          {targetsLabel ? (
            <>
              Scope: <span className="font-mono text-foreground">{targetsLabel}</span>
              {includeNoPorts ? (
                <span className="text-muted-foreground"> · including hosts without open ports</span>
              ) : null}
            </>
          ) : (
            'Scanning your allowlisted range…'
          )}
        </p>
      </div>

      <div className="network-scan-live__log rounded-lg border border-border/60 bg-card/40 px-3 py-2 text-left text-muted-foreground">
        {lines.map((line, i) => (
          <div key={`${line}-${i}`} className="flex gap-2">
            <span className="text-primary/80">{i === 0 ? '›' : '·'}</span>
            <span className={i === 0 ? 'text-foreground' : ''}>{line}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function NetworkScanTerminalBanner({ status, errorText }) {
  if (status === 'completed') {
    return (
      <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-200">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" />
        Scan finished — results below.
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-500/35 bg-red-500/5 px-3 py-2 text-sm text-red-200">
        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
        <span>{errorText || 'Scan failed.'}</span>
      </div>
    );
  }
  return null;
}
